import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter, Prompt } from 'react-router-dom'
import { FormattedMessage, FormattedHTMLMessage, FormattedDate, injectIntl, intlShape } from 'react-intl'
import { toInteger, findIndex, find, filter, each, map, keyBy, set, debounce } from 'lodash'
import moment from 'moment'

import { callApi } from '../services/fetchService'
import { decodePatient } from '../services/encodingService'
import { getSpecificItems, getSelectOptions } from '../services/selectService'
import { openPatientRecord, openConsultation } from '../actions'

import FaIcon from '../components/FaIcon'
import AutoSaveButton from '../components/AutoSaveButton'
import { Spin, Tabs, Table, Card, Button, Row, Col, Input, Select, List, Dropdown, Menu, Popover, Divider, Alert, message } from 'antd'
const { TabPane } = Tabs
const { Column } = Table
const { TextArea } = Input;



/**
 * Plants form
 */

class PlantsForm extends Component {

    static propTypes = {
        consultation: PropTypes.object.isRequired,
        onFieldChange: PropTypes.func.isRequired,
        files: PropTypes.array.isRequired
    }

    constructor(props) {
        super(props)
        this.state = {
            plants: this.props.consultation.plants,
            treatment: this.props.consultation.observations.treatment
        }
    }

    togglePlant(id) {
        let { plants } = this.state
        const { consultation, onFieldChange } = this.props
        if (find(plants, { id })) {
            plants = filter(plants, plant => plant.id !== id)
        } else {
            plants.push({ id })
        }
        this.setState({ plants })
        onFieldChange(consultation.id, { name: 'plants', value: plants })
    }

    changeTreatment = (e) => {
        const { consultation, onFieldChange } = this.props
        const treatment = e.target.value
        this.setState({ treatment })
        onFieldChange(consultation.id, { name: 'observations.treatment', value: treatment })
    }

    changePharmaceuticalForm(id, value) {
        const { plants } = this.state
        const { consultation, onFieldChange } = this.props
        const plant = find(plants, { id })
        if (plant) {
            plant.pharmaceuticalForm = value
            this.setState({ plants })
            onFieldChange(consultation.id, { name: 'plants', value: plants })
        }
    }

    render() {
        const { plants, treatment } = this.state
        const actions = this.props.consultation.actions

        if (!actions || actions.length === 0) {
            return (
                <div className="message" style={{ marginTop: "45px" }}>
                    <FaIcon icon="exclamation-circle" size="8x" />
                    <br /><br />
                    <span style={{ fontSize: "35px" }}><FormattedMessage id="patient.plants.noActions" /></span>
                    <br /><br />
                    <FormattedMessage id="patient.plants.noActions.description" />
                </div>
            )
        }

        const fullPlants = getSpecificItems('plants', map(plants, 'id'))
        const indexedPlants = keyBy(plants, 'id')
        each(fullPlants, (plant) => {
            plant.pharmaceuticalForm = indexedPlants[plant.id].pharmaceuticalForm
        })

        const fullActions = getSpecificItems('actions', actions)
        each(fullActions, (action) => {
            action.plants = getSpecificItems('plants', action.plants)
        })

        const helpMenu = (
            <Menu>
                { this.props.files.map((file) =><Menu.Item key={file.filename}><a target="_blank" href={file.url}>{file.name}</a></Menu.Item>) }
            </Menu>
        );

        return (
            <Row gutter={24}>
                {
                    fullActions.map((action) => (
                        <Col span={8} key={action.id}>
                            <Card title={action.name}>
                                <List itemLayout="horizontal" className="actions-list" dataSource={action.plants} renderItem={(plant) => (
                                    <Popover mouseEnterDelay={0.5} content={
                                        <div style={{ maxWidth: 300 }}>
                                            { plant.description }
                                            <Divider style={{ margin: '15px 0px 10px 0px' }} />
                                            { plant.authorizedPregnancy ? <FaIcon icon="check" color="green" fixedWidth /> : <FaIcon icon="times" color="red" fixedWidth /> } <FormattedMessage id="patient.plants.pregnancy" /><br />
                                            { plant.authorizedBaby ? <FaIcon icon="check" color="green" fixedWidth /> : <FaIcon icon="times" color="red" fixedWidth /> } <FormattedMessage id="patient.plants.baby" /><br />
                                            { plant.authorizedChild ? <FaIcon icon="check" color="green" fixedWidth /> : <FaIcon icon="times" color="red" fixedWidth /> } <FormattedMessage id="patient.plants.child" />
                                        </div>
                                    } trigger="hover">
                                        {
                                            this.props.readonly
                                                ? <List.Item className={"readonly " + (find(plants, { id: plant.id }) ? "selected" : null)}>
                                                    {plant.name}{find(plants, { id: plant.id }) ? <FaIcon style={{ float: "right" }} icon="check" color="green" size="2x" /> : null}
                                                </List.Item>
                                                : <List.Item className={find(plants, { id: plant.id }) ? "selected" : null} onClick={() => this.togglePlant(plant.id)}>
                                                    {plant.name}{find(plants, { id: plant.id }) ? <FaIcon style={{ float: "right" }} icon="check" color="green" size="2x" /> : null}
                                                </List.Item> 
                                        }
                                    </Popover>
                                )} />
                            </Card>
                        </Col>
                    ))
                }
                <Col span={24}>
                    <Card title={<span><FaIcon icon="leaf" /> <FormattedMessage id="patient.plants.selectedPlants" /></span>} extra={<Dropdown overlay={helpMenu}><Button size="small" hidden={this.props.files.length === 0}><FaIcon icon="question-circle"/> Aide <FaIcon icon="angle-down" /></Button></Dropdown>}>
                        { filter(plants, { pharmaceuticalForm: 'he' }).length > 3 ? <Alert message={<FormattedMessage id="patient.plants.warningHe" />} type="warning" showIcon style={{ marginBottom: 15 }} /> : null }
                        <Table dataSource={fullPlants} rowKey="id" size="middle" pagination={false}>
                            <Column title={<FormattedMessage id="patient.plants.plant"/>} dataIndex="name" />
                            <Column title={<FormattedMessage id="patient.plants.pharmaceuticalForm"/>} key="pharmaceuticalForm" render={
                                (plant) => (
                                    <Select style={{ minWidth: 250 }} showSearch value={plant.pharmaceuticalForm} onChange={(value) => this.changePharmaceuticalForm(plant.id, value)} optionFilterProp="children" filterOption={(input, option) => option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0} disabled={this.props.readonly || false}>
                                        {getSelectOptions('pharmaceutical-forms', plant.pharmaceuticalForm, plant.pharmaceuticalForms)}
                                    </Select>
                                )
                            } />
                        </Table>
                    </Card>
                    <Card title={<span><FaIcon icon="pills" /> <FormattedMessage id="patient.observations.treatment" /></span>}>
                        <TextArea autosize={{ minRows: 4 }} defaultValue={treatment} onChange={this.changeTreatment} />
                    </Card>
                </Col>
            </Row>
        )
    }

}



/**
 * Plants view
 */

class PatientPlants extends Component {

    static propTypes = {
        openPatientRecord: PropTypes.func.isRequired,
        openConsultation: PropTypes.func.isRequired,
        match: PropTypes.object.isRequired,
        consultation: PropTypes.object,
        intl: intlShape.isRequired,
        session: PropTypes.object.isRequired
    }
    
    state = {
        patientId: null,
        patient: {},
        consultations: null,
        files: [],
        loading: false,
        activeTab: null,
        dirty: {}
    }

    fetch = () => {
        const { intl, openPatientRecord } = this.props
        this.setState({ loading: true })
        callApi([{
            endpoint: 'patients/' + this.state.patientId
        }, {
            endpoint: 'patients/' + this.state.patientId + '/consultations'
        }, {
            endpoint: 'uploads'
        }]).then((data) => {
            decodePatient(data[0]).then((decoded) => {
                data[0] = decoded
                openPatientRecord(data[0])
                this.setState({
                    patient: data[0],
                    consultations: data[1],
                    files: data[2],
                    loading: false
                })
            })
        }).catch((data) => {
            message.error(intl.formatMessage({id: 'misc.unknownError'}))
            this.setState({ loading: false })
        })
    }

    componentWillReceiveProps(nextProps) {
        let id = toInteger(nextProps.match.params.id)
        if (this.state.patientId !== id) {
            this.setState({ patientId: id }, () => {
                this.fetch()
            })
        }
    }

    componentWillMount() {
        let id = toInteger(this.props.match.params.id)
        this.setState({ patientId: id })
    }

    componentDidMount() {
        this.fetch()
    }

    saveConsultation = (id) => {
        const { intl } = this.props
        const { consultations, dirty } = this.state
        const consultation = consultations[findIndex(consultations, { id })]
        callApi({
            endpoint: 'consultations/' + consultation.id,
            body: consultation,
            method: 'POST'
        }).catch((err) => {
            message.error(intl.formatMessage({id: 'misc.unknownError'}))
        })
        dirty[id] = false
        this.setState({ dirty })
    }

    addConsultation = () => {
        const { intl, openConsultation } = this.props
        const { consultations } = this.state
        if (this.canAddConsultation()) {
            callApi({
                endpoint: 'consultations',
                body: {
                    patient: this.state.patientId,
                    date: Date.now()
                },
                method: 'PUT'
            }).then((data) => {
                consultations.push(data)
                this.setState({
                    consultations: consultations,
                    activeTab: "" + data.id
                })
                openConsultation(data)
                message.success(this.props.intl.formatMessage({id: "misc.updateSuccess"}))
            }).catch((err) => {
                message.error(intl.formatMessage({id: 'misc.unknownError'}))
            })
            
        }
    }

    canAddConsultation = () => {
        const { consultations } = this.state
        if (consultations.length) {
            const lastDate = consultations[consultations.length-1].date
            return !moment(lastDate).isSame(moment(), 'day')
        }
        return true
    }

    onTabsChange = (activeTab) => {
        this.setState({ activeTab: "" + activeTab });
    }

    debouncedFunctions = {}
    onFieldChange = (id, field) => {
        const { consultations, dirty } = this.state
        set(consultations[findIndex(consultations, { id })], field.name, field.value)
        this.setState({
            consultations: consultations
        })
        if (!this.debouncedFunctions[id]) {
            this.debouncedFunctions[id] = debounce(this.saveConsultation, 3 * 1000, { maxWait: 30 * 1000 })
        }
        this.debouncedFunctions[id](id)
        dirty[id] = true
        this.setState({ dirty })
    }

    forceSave = () => {
        const { dirty } = this.state
        map(dirty, (value, key) => {
            if (value === true) {
                this.saveConsultation(Number(key))
            }
        })
    }

    render() {
        const { patient, loading, consultations, activeTab, files, dirty } = this.state
        const { consultation, intl } = this.props
        const { user } = this.props.session

		if (consultations === null || loading) {
			return (
                <div style={{ textAlign: 'center', marginTop: '50px' }}>
                    <Spin size="large" />
                </div>
            )
        }

        const isDirty = !!find(dirty, value => value === true)

        const currentConsultationId = consultation ? consultation.id : null
        const newActiveTab = activeTab || (consultation ? "" + consultation.id : (consultations.length ? "" + consultations[consultations.length - 1].id : null))
        
        return (
            <div>
                <h1><FaIcon icon="user" fixedWidth /><FormattedHTMLMessage id="patient.plants.title" values={patient} /></h1>
                { this.canAddConsultation() ? <div className="action-bar"><Button onClick={this.addConsultation} disabled={!user.canAddConsultations}><FaIcon icon="plus" /> <FormattedMessage id="patient.misc.addConsultation" /></Button></div> : null }
                {
                    !consultations.length ? (
                        <div className="message" style={{ marginTop: "45px" }}>
                            <FaIcon icon="exclamation-circle" size="8x" />
                            <br /><br />
                            <span style={{ fontSize: "35px" }}><FormattedMessage id="patient.misc.noConsultations" /></span>
                            <br /><br />
                            <FormattedMessage id="patient.misc.noConsultations.description" />
                        </div>
                    ) : (
                        <div>
                            <Tabs onChange={this.onTabsChange} activeKey={newActiveTab}>
                                {
                                    consultations.map((consultation, index) => (
                                        <TabPane tab={consultation.id === currentConsultationId 
                                            ? <strong><FaIcon icon="angle-double-right" size="lg" fixedWidth /> <FormattedDate value={consultation.date} /></strong>
                                            : <FormattedDate value={consultation.date} />
                                        } key={"" + consultation.id}>
                                            <PlantsForm consultation={consultation} files={files} onFieldChange={this.onFieldChange} readonly={index !== consultations.length - 1} />
                                        </TabPane>
                                    ))
                                }
                            </Tabs>
                            <AutoSaveButton isDirty={isDirty} onClick={this.forceSave} />
                        </div>
                    )
                }
                <Prompt when={isDirty} message={intl.formatMessage({id: 'misc.unsavedData'})} />
            </div>
        )
    }

}

const mapStateToProps = (state, ownProps) => ({
    consultation: state.consultation,
    session: state.session
})

export default injectIntl(withRouter(connect(mapStateToProps, {
    openPatientRecord,
    openConsultation
})(PatientPlants)))


// WEBPACK FOOTER //
// ./src/views/PatientPlants.js